/**
 * Theme + font boundary — resolves Uniwind CSS variables and the bundled
 * Nunito face into the plain `FloorTheme` / `SkFont` values renderers
 * consume. This is the ONLY engine file that touches app theming (mirrors
 * `components/Chart/hooks/useChartTheme.ts`); everything below it takes
 * resolved strings.
 *
 * @module floorplan/hooks/useFloorTheme
 * @category FloorPlan
 */

import { useMemo } from 'react';
import type { SkFont } from '@shopify/react-native-skia';
import { useFont } from '@shopify/react-native-skia';
import { useCSSVariable } from 'uniwind';

import NunitoSemiBold from '@assets/fonts/NunitoSemiBold.ttf';

import type { FloorTheme } from '../renderer/types';

/** Base size — drawers call `font.setSize(...)` per label anyway. */
const BASE_FONT_SIZE = 14;

export type FloorThemeResult = {
  theme: FloorTheme;
  /** Null until the async font load resolves; label drawing skips until then. */
  font: SkFont | null;
};

const useFloorTheme = (): FloorThemeResult => {
  const background = useCSSVariable('--color-background');
  const card = useCSSVariable('--color-card');
  const border = useCSSVariable('--color-border');
  const foreground = useCSSVariable('--color-foreground');
  const mutedForeground = useCSSVariable('--color-muted-foreground');
  const primary = useCSSVariable('--color-primary');
  const ring = useCSSVariable('--color-ring');

  const font = useFont(NunitoSemiBold, BASE_FONT_SIZE);

  const theme = useMemo<FloorTheme>(
    () => ({
      surface: String(card ?? '#ffffff'),
      grid: String(border ?? '#e5e7eb'),
      objectFill: String(background ?? '#f4f4f5'),
      objectStroke: String(mutedForeground ?? '#71717a'),
      wall: String(foreground ?? '#3f3f46'),
      label: String(foreground ?? '#18181b'),
      accent: String(primary ?? '#166534'),
      selection: String(ring ?? primary ?? '#166534'),
    }),
    [background, card, border, foreground, mutedForeground, primary, ring],
  );

  return { theme, font };
};

export default useFloorTheme;
